import React, { useState, useEffect } from 'react';

// グローバル関数の型定義
declare global {
  interface Window {
    showGlobalFlash?: (message: string, type: string, htmlSafe: boolean) => void;
  }
}

interface JobListingCardProps {
  jobListing: {
    id: number;
    title: string;
    max_reward: number | null;
    min_reward: number | null;
    contract_type: string;
    prefecture: string | { id: number; name: string } | null;
    city: string | null;
    station: string | null;
    workstyle_type: number;
    workstyle_type_label: string | null;
    skills: Array<{ id: number; name: string }>;
    occupations: Array<{ id: number; name: string }>;
    characteristics: Array<{ id: number; name: string }>;
    industries?: Array<{ id: number; name: string }>;
    availability_type?: number;
    status?: string;
    job_source: { id: number; name: string; display_name?: string };
    published_at: string;
    created_at: string;
    is_new: boolean;
    is_favorited: boolean;
    description?: string | null;
  };
  onClick: (jobId: number) => void;
  isSelected?: boolean;
  initialIsMobile?: boolean;
}

const JobListingCard: React.FC<JobListingCardProps> = ({ 
  jobListing, 
  onClick, 
  isSelected = false,
  initialIsMobile = false
}) => {
  // データの存在チェック
  if (!jobListing) {
    return null;
  }

  // 求人の保存状態を管理
  const [isFavorited, setIsFavorited] = useState(jobListing.is_favorited || false);
  
  // モバイル判定をstateで管理（SSRとクライアント側で同じ初期HTMLを生成するため、初期値はinitialIsMobileを使用）
  // Initialize with server-side value to keep SSR/CSR markup identical
  const [isMobile, setIsMobile] = useState<boolean>(initialIsMobile);
  
  // Hydration完了を検知するフラグ（hydration error対策）
  const [isHydrated, setIsHydrated] = useState(false);

  // Hydration完了後にモバイル判定を実行（hydration error対策）
  useEffect(() => {
    // Hydration完了をマーク
    setIsHydrated(true);
    
    const checkIsMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    // Hydration完了後に初回判定（setTimeoutで十分な遅延を設けてhydration完了を保証）
    // 100msの遅延を設けることで、Reactのhydration処理が完全に完了した後に実行される
    const timeoutId = setTimeout(() => {
      checkIsMobile();
    }, 100);
    
    // リサイズ時に再判定
    window.addEventListener('resize', checkIsMobile);
    
    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('resize', checkIsMobile);
    };
  }, []);

  /**
   * 求人の保存をトグルする
   * @param e クリックイベント
   */
  const handleFavoriteClick = async (e: React.MouseEvent) => {
    e.preventDefault(); // デフォルト動作を防ぐ
    e.stopPropagation(); // カードクリックを防ぐ
    
    try {
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '';
      
      if (isFavorited) {
        // DELETE /favorites/:id
        const response = await fetch(`/favorites/${jobListing.id}`, {
          method: 'DELETE',
          headers: { 
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'X-CSRF-Token': csrfToken
          }
        });
        
        if (response.ok) {
          setIsFavorited(false);
          // フラッシュメッセージを表示
          if (window.showGlobalFlash) {
            window.showGlobalFlash('求人を保存から削除しました', 'danger', false);
          }
        } else {
          console.error('Failed to remove favorite:', response.status, response.statusText);
          // エラーメッセージを表示
          if (window.showGlobalFlash) {
            window.showGlobalFlash('エラーが発生しました。再度お試しください。', 'error', false);
          }
        }
      } else {
        // POST /favorites
        const response = await fetch('/favorites', {
          method: 'POST',
          headers: { 
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'X-CSRF-Token': csrfToken
          },
          body: JSON.stringify({ job_listing_id: jobListing.id })
        });
        
        if (response.ok) {
          setIsFavorited(true);
          // フラッシュメッセージを表示
          if (window.showGlobalFlash) {
            window.showGlobalFlash('求人を保存しました', 'success', false);
          }
        } else {
          console.error('Failed to add favorite:', response.status, response.statusText);
          // エラーメッセージを表示
          if (window.showGlobalFlash) {
            window.showGlobalFlash('エラーが発生しました。再度お試しください。', 'error', false);
          }
        }
      }
    } catch (error) {
      console.error('Failed to toggle favorite:', error);
      // エラーメッセージを表示
      if (window.showGlobalFlash) {
        window.showGlobalFlash('エラーが発生しました。再度お試しください。', 'error', false);
      }
    }
  };

  // Format number with thousands separators in a deterministic way (no locale impact)
  const formatNumber = (num: number): string => num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');

  const formatSalary = (min: number | null, max: number | null) => {
    if (min && max) {
      return formatNumber(max);
    }
    if (min) {
      return formatNumber(min);
    }
    if (max) {
      return formatNumber(max);
    }
    return '要相談';
  };

  /**
   * 日付を固定フォーマットに変換する（SSR時のhydration error対策）
   * @param dateString ISO8601形式の日付文字列
   * @returns YYYY-MM-DD形式の日付文字列
   */
  const formatDate = (dateString: string) => {
    // Assume ISO8601; slice is deterministic and avoids timezone discrepancies
    return dateString ? dateString.slice(0, 10) : '日付不明';
  };

  /**
   * 勤務地情報を構築する
   * @returns 勤務地文字列
   */
  const buildLocationString = () => {
    const getPrefectureName = () => {
      if (!jobListing?.prefecture) return null;
      if (typeof jobListing.prefecture === 'object' && jobListing.prefecture !== null) {
        return jobListing.prefecture.name;
      }
      return jobListing.prefecture;
    };

    const locationParts = [
      getPrefectureName(),
      jobListing?.city,
      jobListing?.station
    ].filter(Boolean);
    
    return locationParts.join(' ');
  };


  return (
    <div 
      className={`card mb-2 job-listing-card ${isSelected ? 'border-primary' : ''}`}
      style={{ 
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        border: isSelected ? '2px solid #007bff' : '1px solid #dee2e6',
        boxShadow: isSelected ? '0 4px 8px rgba(0, 123, 255, 0.2)' : '0 2px 4px rgba(0,0,0,0.1)'
      }}
      onClick={() => {
        if (isMobile) {
          // モバイルの場合はページ遷移
          window.location.href = `/job_listings/${jobListing?.id || 0}`;
        } else {
          // デスクトップの場合は既存のSPA的な詳細表示
          onClick(jobListing?.id || 0);
        }
      }}
    >
      <div className="card-body p-3">
        {/* ヘッダー部分（NEWラベルと保存ボタン） */}
        <div className="d-flex justify-content-between align-items-start mb-2">
          <div className="d-flex gap-1">
            {jobListing?.is_new && (
              <span className="badge bg-danger" style={{ fontSize: '0.7rem' }}>
                NEW
              </span>
            )}
          </div>
          {/* デスクトップのみ保存ボタンを表示 */}
          <button
            className="btn btn-link p-0 d-none d-md-block"
            style={{ 
              color: isFavorited ? '#dc3545' : '#6c757d', 
              flexShrink: 0,
              transition: 'color 0.2s ease'
            }}
            onClick={handleFavoriteClick}
          >
            <i className={isFavorited ? 'fas fa-bookmark' : 'far fa-bookmark'}></i>
          </button>
        </div>

        {/* タイトル */}
        <div className="mb-2">
          <h3 className="card-title mb-0 fw-bold" style={{ fontSize: '0.9rem', lineHeight: '1.2', whiteSpace: 'pre-line' }}>
            {jobListing?.title || 'タイトル不明'}
          </h3>
        </div>

        {/* 報酬 */}
        <div className="mb-3">
          <span className="fw-bold" style={{ fontSize: '0.9rem' }}>
            <i className="fas fa-yen-sign me-1 text-muted"></i>
            <span className="text-danger">{formatSalary(jobListing?.min_reward, jobListing?.max_reward)}</span>
            <span className="text-muted">円/月</span>
          </span>
        </div>

        {/* 特徴リンク */}
        {(() => {
          const characteristics = Array.isArray(jobListing?.characteristics) ? jobListing.characteristics : [];
          return characteristics.length > 0 && (
            <div className="mb-3">
              <div className="d-flex align-items-center flex-wrap gap-1">
                <i className="fas fa-tags me-1 text-muted" style={{ fontSize: '0.7rem', width: '12px', height: '12px', textAlign: 'center' }}></i>
                {characteristics.map((characteristic, index) => (
                  <React.Fragment key={index}>
                    <a
                      href={`/characteristics/${characteristic?.id || '#'}`}
                      className="text-decoration-none text-primary"
                    >
                      {characteristic?.name || 'Unknown'}
                    </a>
                    {index < characteristics.length - 1 && (
                      <span className="text-muted">/</span>
                    )}
                  </React.Fragment>
                ))}
              </div>
            </div>
          );
        })()}

        {/* 契約形態 */}
        <div className="mb-3">
          <div className="d-flex align-items-center flex-wrap gap-1">
            <i className="fas fa-briefcase me-1 text-muted" style={{ width: '12px', height: '12px', textAlign: 'center' }}></i>
            <span className="text-muted small">{jobListing?.contract_type || '業務委託(フリーランス)'}</span>
          </div>
        </div>
        
        {/* 勤務地 */}
        {buildLocationString() && (
          <div className="mb-3">
            <div className="text-muted small">
              <i className="fas fa-map-marker-alt me-1" style={{ width: '12px', height: '12px', textAlign: 'center' }}></i>
              {buildLocationString()}
            </div>
          </div>
        )}

        {/* スキルリンク */}
        {(() => {
          const skills = Array.isArray(jobListing?.skills) ? jobListing.skills : [];
          return skills.length > 0 && (
            <div className="mb-3">
              <div className="d-flex align-items-center flex-wrap gap-1">
                <i className="fas fa-code me-1 text-muted" style={{ fontSize: '0.7rem', width: '12px', height: '12px', textAlign: 'center' }}></i>
                {skills.map((skill, index) => (
                  <React.Fragment key={index}>
                    <a
                      href={`/skills/${skill?.id || '#'}`}
                      className="text-decoration-none text-primary"
                    >
                      {skill?.name || 'Unknown'}
                    </a>
                    {index < skills.length - 1 && (
                      <span className="text-muted">/</span>
                    )}
                  </React.Fragment>
                ))}
              </div>
            </div>
          );
        })()}

        {/* 職種リンク */}
        {(() => {
          const occupations = Array.isArray(jobListing?.occupations) ? jobListing.occupations : [];
          return occupations.length > 0 && (
            <div className="mb-3">
              <div className="d-flex align-items-center flex-wrap gap-1">
                <i className="fas fa-user-tie me-1 text-muted" style={{ fontSize: '0.7rem', width: '12px', height: '12px', textAlign: 'center' }}></i>
                {occupations.map((occupation, index) => (
                  <React.Fragment key={index}>
                    <a
                      href={`/occupations/${occupation?.id || '#'}`}
                      className="text-decoration-none text-primary"
                    >
                      {occupation?.name || 'Unknown'}
                    </a>
                    {index < occupations.length - 1 && (
                      <span className="text-muted">/</span>
                    )}
                  </React.Fragment>
                ))}
              </div>
            </div>
          );
        })()}

        {/* 説明文（スマホのみ） */}
        {jobListing?.description && (
          <div className="mb-3 d-block d-md-none">
            <p 
              className="mb-0 text-muted" 
              style={{ 
                fontSize: '0.75rem', 
                lineHeight: '1.5', 
                display: '-webkit-box',
                WebkitLineClamp: 3,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                wordBreak: 'break-word',
                whiteSpace: 'normal'
              }}
            >
              {(() => {
                // 行頭の空白を削除し、各行の連続するスペース・タブを単一に（改行は保持）
                const cleanedDescription = jobListing.description
                  .split('\n')
                  .map(line => line.trimStart().replace(/[ \t]+/g, ' '))
                  .join('\n')
                  .trim();
                return cleanedDescription;
              })()}
            </p>
          </div>
        )}

        {/* フッター */}
        <div className="text-muted" style={{ fontSize: '0.75rem' }}>
          <div>
            提供元：{jobListing?.job_source?.display_name || jobListing?.job_source?.name || '不明'}
          </div>
        </div>

        {/* スマホの場合：区切り線とアクションボタン */}
        <div className="d-block d-md-none">
          <hr className="my-2" />
          <div className="d-flex gap-2 align-items-center">
            {/* 保存ボタン */}
            <button
              className="btn btn-secondary"
              style={{ 
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.5rem',
                padding: '10px',
                fontWeight: 'bold',
                backgroundColor: isFavorited ? undefined : 'white',
                borderColor: isFavorited ? undefined : '#00bb94',
                color: isFavorited ? undefined : '#00bb94'
              }}
              onClick={handleFavoriteClick}
            >
              <i className={isFavorited ? 'fas fa-bookmark' : 'far fa-bookmark'} style={{ fontSize: '1.2rem' }}></i>
              <span>保存する</span>
            </button>
            {/* 応募ボタン */}
            <button
              className="btn btn-primary flex-grow-1"
              style={{
                fontWeight: 'bold',
                padding: '10px'
              }}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                window.location.href = `/job_listings/${jobListing?.id || 0}`;
              }}
            >
              詳細を見る
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default JobListingCard;
